import React, { FC, ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ShowUpdatedTariff } from './ShowUpdatedTariff';
import cx from 'classnames';
import { apiManager } from '../../../../REST';
import { IMeta, INameObjectN } from '../../../../interfaces';
import { mapApi2UiList } from '../../../../instruments';
import { tF, tT } from '../../../../translate';
import { ILoadMoreResult, InfiniteTable, LinkComponent } from '../../../../Components';
import TariffsStyles from '../../m_styles.less';

type IListResponse = {
    cards?: any[];
    meta?: IMeta;
};

type ITariffCard = {
    id?: number;
    name?: string;
    currentTariff?: INameObjectN;
    iccId?: string;
    msisdn?: string;
    nextTariff?: INameObjectN;
    printedId?: string;
    shortId?: string;
};

type IDataType = {
    cardLink: ReactNode;
    msisdnLink: ReactNode;
    currentTariffLink: ReactNode;
    nextTariffLink: ReactNode;
};

const columns: ColumnsType<IDataType> = [
    {
        title: tF('Card'),
        dataIndex: 'cardLink',
        className: TariffsStyles.tariffCol,
    },
    {
        title: tF('MSISDN'),
        dataIndex: 'msisdnLink',
        width: 140,
        className: TariffsStyles.tariffCol,
    },
    {
        title: tF('Current month'),
        dataIndex: 'currentTariffLink',
        className: TariffsStyles.tariffCol,
    },
    {
        title: tF('Next month'),
        dataIndex: 'nextTariffLink',
        className: TariffsStyles.tariffCol,
    },
];
interface IAssignTariffCards {
    cardSetsId?: string[] | undefined;
    cardsId?: string[] | undefined;
    companyId?: string | undefined;
    isHide: boolean;
    setCardCount: (count: number) => void;
    selectedTariff?: INameObjectN;
}

export const AssignTariffCards: FC<IAssignTariffCards> = observer(
    ({ isHide, companyId, cardsId, cardSetsId, setCardCount, selectedTariff }: IAssignTariffCards) => {
        const [recordsCount, setRecordsCount] = useState(0);
        const [list, setList] = useState<ITariffCard[]>([]);
        const pageSize = 10;

        const loadMore = async (pageNumber = 1): Promise<ILoadMoreResult> => {
            try {
                const { resp } = await apiManager.getApi('GET_TARIFF_CARDS', {
                    searchParamsList: {
                        pageNumber,
                        pageSize,
                        assignedCompanyId: companyId,
                        ids: cardsId,
                        cardSetsId: cardSetsId,
                    },
                });
                const { cards, meta } = resp as IListResponse;
                setRecordsCount(meta?.records_count || 0);
                const list = mapApi2UiList(cards) as ITariffCard[];

                setList((prevList) => [...prevList, ...list]);
                return {
                    totalCount: meta?.records_count || 0,
                };
            } catch (error) {
                console.error('Error loading list:', error);
                return {
                    totalCount: 0,
                };
            }
        };

        useEffect(() => {
            setList([]);
            setRecordsCount(0);
            if (!!companyId || cardsId?.length || cardSetsId?.length) {
                loadMore();
            }
        }, [companyId, cardsId, cardSetsId]);

        useEffect(() => {
            setCardCount(recordsCount);
        }, [recordsCount]);

        const dataSource: IDataType[] = list.map((card: ITariffCard) => {
            const cardLinkName = `${
                card.name && card.shortId
                    ? card.name + ' (' + tF('Short ID') + ': ' + card.shortId + ')'
                    : card.name
                    ? card.name
                    : card.shortId
                    ? tF('Short ID') + ': ' + card.shortId
                    : card.name || card.shortId || card.printedId
            }`;
            const cardLinkTooltip = `${tF('ID')}: ${card.id} ${card.name ? ', ' + tF('Name') + ': ' + card.name : ''} ${
                card.shortId ? ', ' + tF('Short ID') + ': ' + card.shortId : ''
            } ${', ' + tF('Printed ID')} ${card.printedId}`;
            return {
                key: card.id,
                cardLink: (
                    <LinkComponent
                        currentListComponent={'CardList'}
                        id={card.id}
                        name={cardLinkName}
                        tooltip={cardLinkTooltip}
                    />
                ),
                msisdnLink: card.msisdn ? (
                    <LinkComponent currentListComponent={'CardList'} id={card.id} name={card.msisdn} />
                ) : null,
                currentTariffLink: (
                    <ShowUpdatedTariff
                        isCurrentMonth
                        currentTariff={card.currentTariff}
                        selectedTariff={selectedTariff}
                    />
                ),
                nextTariffLink: (
                    <ShowUpdatedTariff
                        isCurrentMonth={false}
                        currentTariff={card.nextTariff}
                        selectedTariff={selectedTariff}
                    />
                ),
            };
        });

        const isCardsWithExistingTariff = list.some((card) => !!card.currentTariff);

        return (
            <div className={cx(TariffsStyles.assignTariffFormContainer, { [TariffsStyles.hideDiv]: isHide })}>
                {isCardsWithExistingTariff ? (
                    <Alert
                        type="warning"
                        showIcon
                        message={tT(
                            'The following cards already have tariffs, review the changes which will be applied',
                        )}
                        className={TariffsStyles.assignTariffWarning}
                    />
                ) : null}
                <InfiniteTable<IDataType>
                    columns={columns}
                    dataSource={dataSource}
                    idSpinners={['GET_TARIFF_CARDS']}
                    loadMore={loadMore}
                    resetKey={`${companyId}-${cardsId?.join(',')}-${cardSetsId?.join(',')}`}
                    scrollY={420}
                />
            </div>
        );
    },
);
